import React, { useEffect, useState } from "react";
import { Plus, Play, Lock } from "lucide-react";
import { toast } from "../ui/Toast";

// Reusable salary-review cycle controls (used by Session A grid + Session B tab).
// Lists cycles and lets the caller pick the active one (onSelectCycle). Admin-only
// controls create a draft cycle, OPEN a draft (pre-populates allocations), and
// CLOSE an open cycle. Apply is NOT surfaced here (deferred to Session B's
// pending-payroll panel — and never actually called). Cycle status: draft|open|
// closed|applied.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BASE = `${API_BASE}/api/payroll/salary-review`;
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  draft:   "text-white/50 bg-white/5 border-white/15",
  open:    "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  closed:  "text-amber-400 bg-amber-500/10 border-amber-500/25",
  applied: "text-blue-400 bg-blue-500/10 border-blue-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { draft: "Draft", open: "Open", closed: "Closed", applied: "Applied" };

const isAdminRole = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    const roles = [u?.role, ...String(u?.roles || "").split(",")].map(r => (r || "").trim().toLowerCase());
    return roles.includes("admin") || roles.includes("superadmin");
  } catch { return false; }
};

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const EMPTY = { name: "", total_budget_pct: "", opens_at: "", closes_at: "", effective_date: "" };

export default function SalaryCycleControls({ selectedCycleId, onSelectCycle }) {
  const [cycles, setCycles]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [busyId, setBusyId]     = useState(null);

  const admin = isAdminRole();
  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, "Content-Type": "application/json" };

  const load = async (autoSelect = false) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/cycles`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      const list = Array.isArray(data) ? data : [];
      setCycles(list);
      if (autoSelect && !selectedCycleId && list.length) {
        onSelectCycle?.(list.find(c => c.status === "open") || list[0]);
      }
    } catch { setCycles([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(true); }, []); // eslint-disable-line

  const create = async () => {
    if (!form.name.trim()) { toast.error("Cycle name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name,
        total_budget_pct: form.total_budget_pct === "" ? null : Number(form.total_budget_pct),
        opens_at: form.opens_at || null,
        closes_at: form.closes_at || null,
        effective_date: form.effective_date || null,
      };
      const res = await fetch(`${BASE}/cycles`, { method: "POST", headers: jsonH, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Cycle created (draft)");
      setForm(EMPTY); setShowCreate(false);
      await load();
    } catch { toast.error("Failed to create cycle"); }
    finally { setSaving(false); }
  };

  const openCycle = async (c) => {
    setBusyId(c.id);
    try {
      const res = await fetch(`${BASE}/cycles/${c.id}/open`, { method: "PATCH", headers: authH });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      toast.success(`Cycle opened — ${d.allocations_created ?? 0} allocations created`);
      await load();
      onSelectCycle?.({ ...c, status: "open" });
    } catch { toast.error("Failed to open cycle"); }
    finally { setBusyId(null); }
  };

  const closeCycle = async (c) => {
    setBusyId(c.id);
    try {
      const res = await fetch(`${BASE}/cycles/${c.id}/close`, { method: "POST", headers: authH });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Cycle closed");
      await load();
      onSelectCycle?.({ ...c, status: "closed" });
    } catch { toast.error("Failed to close cycle"); }
    finally { setBusyId(null); }
  };

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Salary Review Cycles</span>
        {admin && (
          <button onClick={() => setShowCreate(s => !s)}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> New Cycle
          </button>
        )}
      </div>

      {admin && showCreate && (
        <div className="px-5 py-4 border-b border-white/5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className={labelCls}>Cycle Name</label>
              <input className={inputCls} value={form.name} placeholder="e.g. FY26 Annual Review"
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><label className={labelCls}>Total Budget %</label>
              <input type="number" className={inputCls} value={form.total_budget_pct}
                onChange={e => setForm(f => ({ ...f, total_budget_pct: e.target.value }))} /></div>
            <div><label className={labelCls}>Opens At</label>
              <input type="datetime-local" className={inputCls} value={form.opens_at}
                onChange={e => setForm(f => ({ ...f, opens_at: e.target.value }))} /></div>
            <div><label className={labelCls}>Closes At</label>
              <input type="datetime-local" className={inputCls} value={form.closes_at}
                onChange={e => setForm(f => ({ ...f, closes_at: e.target.value }))} /></div>
            <div><label className={labelCls}>Effective Date</label>
              <input type="date" className={inputCls} value={form.effective_date}
                onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))} /></div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowCreate(false); setForm(EMPTY); }}
              className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={create} disabled={saving || !form.name.trim()}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Creating…" : "Create Cycle"}
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-white/5">
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => <div key={i} className="px-5 py-3"><div className="h-8 rounded-lg bg-white/5 animate-pulse" /></div>)
        ) : cycles.length === 0 ? (
          <p className="px-5 py-6 text-white/20 text-sm italic text-center">No salary-review cycles yet.</p>
        ) : cycles.map(c => {
          const active = c.id === selectedCycleId;
          return (
            <div key={c.id}
              className={`flex items-center justify-between gap-3 px-5 py-3 cursor-pointer transition-colors ${active ? "bg-[#00ff90]/5" : "hover:bg-white/3"}`}
              onClick={() => onSelectCycle?.(c)}>
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <span className={`w-2 h-2 rounded-full ${active ? "bg-[#00ff90]" : "bg-transparent border border-white/20"}`} />
                <span className="text-sm text-white/80 font-medium truncate">{c.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[c.status] || STATUS_FALLBACK}`}>{STATUS_LABEL[c.status] || c.status}</span>
                <span className="text-xs text-white/25">Eff. {fmtDate(c.effective_date)}</span>
                {c.total_budget_pct != null && <span className="text-xs text-white/25">· Budget {c.total_budget_pct}%</span>}
              </div>
              {admin && (
                <div className="flex gap-1.5 shrink-0">
                  {c.status === "draft" && (
                    <button onClick={(e) => { e.stopPropagation(); openCycle(c); }} disabled={busyId === c.id}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 disabled:opacity-40 transition-colors">
                      <Play size={11} /> {busyId === c.id ? "Opening…" : "Open"}
                    </button>
                  )}
                  {c.status === "open" && (
                    <button onClick={(e) => { e.stopPropagation(); closeCycle(c); }} disabled={busyId === c.id}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-amber-500/25 bg-amber-500/5 text-amber-300 hover:bg-amber-500/15 disabled:opacity-40 transition-colors">
                      <Lock size={11} /> {busyId === c.id ? "Closing…" : "Close"}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
